'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { Users, Shield, UserCheck, UserX, RefreshCw, Search, Mail, Crown, User, MoreVertical, CheckCircle, XCircle,  } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import LoadingSkeleton from '@/components/ui/LoadingSkeleton';

interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'member';
  isActive: boolean;
  createdAt: string;
}

function mapProfile(r: any): UserProfile {
  return {
    id: r.id,
    email: r.email,
    fullName: r.full_name || '',
    role: r.role as 'admin' | 'member',
    isActive: r.is_active ?? true,
    createdAt: r.created_at,
  };
}

export default function AdminPanelClient() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'member'>('all');
  const [editTarget, setEditTarget] = useState<UserProfile | null>(null);
  const [editRole, setEditRole] = useState<'admin' | 'member'>('member');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const supabase = createClient();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id ?? null);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setUsers((data || []).map(mapProfile));
    } catch (err: any) {
      toast.error('Failed to load users: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const filtered = users.filter((u) => {
    const matchSearch =
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.fullName.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const openEdit = (user: UserProfile) => {
    setEditTarget(user);
    setEditRole(user.role);
    setEditActive(user.isActive);
    setOpenMenuId(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          role: editRole,
          is_active: editActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editTarget.id);

      if (error) throw error;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editTarget.id ? { ...u, role: editRole, isActive: editActive } : u
        )
      );
      toast.success(`${editTarget.fullName || editTarget.email} updated successfully`);
      setEditTarget(null);
    } catch (err: any) {
      toast.error('Failed to update user: ' + (err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickRoleToggle = async (user: UserProfile) => {
    const newRole = user.role === 'admin' ? 'member' : 'admin';
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) throw error;
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: newRole } : u)));
      toast.success(`${user.fullName || user.email} is now ${newRole}`);
    } catch (err: any) {
      toast.error('Failed to update role');
    }
    setOpenMenuId(null);
  };

  const handleToggleActive = async (user: UserProfile) => {
    const newActive = !user.isActive;
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: newActive, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) throw error;
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, isActive: newActive } : u)));
      toast.success(`${user.fullName || user.email} ${newActive ? 'activated' : 'deactivated'}`);
    } catch (err: any) {
      toast.error('Failed to update status');
    }
    setOpenMenuId(null);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    setInviting(true);
    try {
      const { data, error } = await supabase.auth.admin
        ? // Try admin invite if available
          { data: null, error: { message: 'use_signUp' } }
        : { data: null, error: { message: 'use_signUp' } };

      // Use signUp with a temp password approach — creates the auth user
      const tempPassword = `TempPass${Math.random().toString(36).slice(2, 10)}!`;
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: inviteEmail.trim(),
        password: tempPassword,
        options: {
          data: {
            full_name: inviteFullName.trim() || inviteEmail.split('@')[0],
            role: inviteRole,
          },
        },
      });

      if (signUpError) throw signUpError;

      // If trigger didn't create profile, insert manually
      if (signUpData?.user) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert({
            id: signUpData.user.id,
            email: inviteEmail.trim(),
            full_name: inviteFullName.trim() || inviteEmail.split('@')[0],
            role: inviteRole,
            is_active: true,
          }, { onConflict: 'id' });

        if (profileError && !profileError.message.includes('duplicate')) {
          throw profileError;
        }
      }

      toast.success(`User ${inviteEmail} created. They can log in and reset their password.`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('member');
      await loadUsers();
    } catch (err: any) {
      toast.error('Failed to create user: ' + (err?.message || 'Unknown error'));
    } finally {
      setInviting(false);
    }
  };

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const activeCount = users.filter((u) => u.isActive).length;

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage user accounts and roles without touching the Supabase dashboard
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadUsers}
            className="btn-secondary gap-1.5"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button onClick={() => setInviteOpen(true)} className="btn-primary gap-1.5">
            <Mail size={14} />
            Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(91,53,232,0.12), rgba(139,92,246,0.12))' }}>
            <Users size={18} style={{ color: '#5b35e8' }} />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{users.length}</p>
            <p className="text-xs text-muted-foreground">Total Users</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(255,92,43,0.12), rgba(255,140,80,0.12))' }}>
            <Crown size={18} style={{ color: '#ff5c2b' }} />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{adminCount}</p>
            <p className="text-xs text-muted-foreground">Admins</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(52,211,153,0.12))' }}>
            <UserCheck size={18} className="text-emerald-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{activeCount}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-8 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-1 border border-border rounded-lg overflow-hidden">
            {(['all', 'admin', 'member'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRoleFilter(r)}
                className={`px-3 py-1.5 text-xs font-medium transition-all ${
                  roleFilter === r
                    ? 'text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                }`}
                style={roleFilter === r ? { background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' } : {}}
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">
            Users
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {filtered.length} of {users.length}
            </span>
          </h2>
          <Shield size={15} className="text-muted-foreground" />
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <LoadingSkeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Users size={32} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No users found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((user) => {
              const initials = user.fullName
                ? user.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
                : user.email.slice(0, 2).toUpperCase();
              const isSelf = user.id === currentUserId;

              return (
                <div
                  key={user.id}
                  className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/20 transition-colors"
                >
                  {/* Avatar */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                    style={{ background: user.role === 'admin' ?'linear-gradient(135deg, #5b35e8, #ff5c2b)' :'linear-gradient(135deg, #64748b, #94a3b8)' }}
                  >
                    {initials}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {user.fullName || '—'}
                      </p>
                      {isSelf && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>

                  {/* Role badge */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {user.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                        style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)' }}>
                        <Crown size={10} />
                        Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
                        <User size={10} />
                        Member
                      </span>
                    )}

                    {/* Active status */}
                    {user.isActive ? (
                      <CheckCircle size={14} className="text-emerald-500" title="Active" />
                    ) : (
                      <XCircle size={14} className="text-red-400" title="Inactive" />
                    )}

                    {/* Joined date */}
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>

                    {/* Actions menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === user.id ? null : user.id);
                        }}
                        className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all"
                        title="Actions"
                      >
                        <MoreVertical size={14} className="text-muted-foreground" />
                      </button>

                      {openMenuId === user.id && (
                        <div
                          className="absolute right-0 top-8 z-20 w-44 bg-card border border-border rounded-xl shadow-lg py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => openEdit(user)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex items-center gap-2"
                          >
                            <Shield size={12} className="text-primary" />
                            Edit Role & Status
                          </button>
                          {!isSelf && (
                            <>
                              <button
                                onClick={() => handleQuickRoleToggle(user)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex items-center gap-2"
                              >
                                <Crown size={12} className="text-amber-500" />
                                Make {user.role === 'admin' ? 'Member' : 'Admin'}
                              </button>
                              <button
                                onClick={() => handleToggleActive(user)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex items-center gap-2"
                              >
                                {user.isActive ? (
                                  <><UserX size={12} className="text-red-500" /> Deactivate</>
                                ) : (
                                  <><UserCheck size={12} className="text-emerald-500" /> Activate</>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={`Edit User — ${editTarget?.fullName || editTarget?.email}`}
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)' }}
            >
              {(editTarget?.fullName || editTarget?.email || '').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{editTarget?.fullName || '—'}</p>
              <p className="text-xs text-muted-foreground">{editTarget?.email}</p>
            </div>
          </div>

          <div>
            <label className="label-base" htmlFor="editRoleSelect">Role</label>
            <p className="text-xs text-muted-foreground mb-1.5">
              Admins have full access including this panel. Members can create and send campaigns.
            </p>
            <select
              id="editRoleSelect"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value as 'admin' | 'member')}
              className="input-base"
              disabled={editTarget?.id === currentUserId}
            >
              <option value="member">Member — campaign access only</option>
              <option value="admin">Admin — full account access</option>
            </select>
            {editTarget?.id === currentUserId && (
              <p className="text-xs text-amber-600 mt-1">You cannot change your own role.</p>
            )}
          </div>

          <div>
            <label className="label-base">Account Status</label>
            <div className="flex items-center gap-3 mt-1.5">
              <button
                onClick={() => setEditActive(true)}
                className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  editActive
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' :'border-border text-muted-foreground hover:bg-muted/30'
                }`}
                disabled={editTarget?.id === currentUserId}
              >
                <CheckCircle size={12} />
                Active
              </button>
              <button
                onClick={() => setEditActive(false)}
                className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  !editActive
                    ? 'border-red-400 bg-red-50 text-red-600' :'border-border text-muted-foreground hover:bg-muted/30'
                }`}
                disabled={editTarget?.id === currentUserId}
              >
                <XCircle size={12} />
                Inactive
              </button>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t border-border">
            <button onClick={() => setEditTarget(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleSaveEdit} className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Invite / Add User Modal */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Add New User"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
            <p className="text-xs text-amber-700">
              This creates a new Supabase auth account. The user will need to reset their password via the login screen.
            </p>
          </div>

          <div>
            <label className="label-base" htmlFor="inviteFullNameField">Full Name</label>
            <input
              id="inviteFullNameField"
              type="text"
              value={inviteFullName}
              onChange={(e) => setInviteFullName(e.target.value)}
              className="input-base"
              placeholder="Jane Smith"
            />
          </div>

          <div>
            <label className="label-base" htmlFor="inviteEmailField">Email Address</label>
            <input
              id="inviteEmailField"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="input-base"
              placeholder="jane@company.com"
            />
          </div>

          <div>
            <label className="label-base" htmlFor="inviteRoleField">Role</label>
            <select
              id="inviteRoleField"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
              className="input-base"
            >
              <option value="member">Member — can create and send campaigns</option>
              <option value="admin">Admin — full account access</option>
            </select>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t border-border">
            <button onClick={() => setInviteOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleInvite} className="btn-primary" disabled={inviting}>
              {inviting ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
